// Sidebar.tsx
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { isAdminRole, isMainAccountLevel, canSeeAdminContent, hasFullPermissions } from '../config/permissionsConfig';
import { logoutSuperadminToLogin } from '../utils/superadminAuth';
import { createForceLogout } from '../utils/forceLogout';
import {
  FiBell,
  FiBriefcase,
  FiCalendar,
  FiCheckSquare,
  FiClock,
  FiFileText,
  FiLogOut,
  FiMap,
  FiMessageSquare,
  FiMonitor,
  FiServer,
  FiSettings,
  FiShoppingCart,
  FiSliders,
  FiUsers,
  FiBarChart2,
  FiX,
  FiHelpCircle,
  FiChevronLeft,
  FiChevronRight,
  FiPackage,
  FiAlertTriangle,
  FiDatabase,
  FiCloud,
  FiActivity,
  FiZap,
  FiRepeat,
  FiAward,
  FiStar,
  FiLock,
  FiDollarSign,
  FiShield,
  FiDroplet,
  FiMail,
  FiUser,
  FiTruck
} from 'react-icons/fi';
import { FaCarSide, FaUsers as FaUsersIcon } from 'react-icons/fa';
import { IoCalendarNumberOutline, IoSettingsOutline } from 'react-icons/io5';
import { FcAbout } from 'react-icons/fc';
import { LiaTasksSolid } from 'react-icons/lia';
import { useClient } from '../contexts/ClientContext';
import { logout } from '../api/authApi';
import { useCompanyFeatures } from '../contexts/CompanyFeaturesContext';
import { LogoutOverlay } from './auth/LogoutOverlay';
import { queryClient, queryOptions } from '../lib/queryClient';
import { useUnsavedChanges } from '../guards/useUnsavedChangesGuard';
import { contractKeys } from '../lib/queryKeys';
import { getContracts } from '../api/contractsApi';
import { lazyWithPreload } from '../utils/lazyWithPreload';
import { SiGoogleads } from 'react-icons/si';
import {
  prefetchDashboard,
  prefetchClients,
  prefetchAgents,
  prefetchManagerTasks,
  prefetchManagerLeads,
  prefetchManagers,
  prefetchAdminTasks,
  prefetchAdminLeads,
  prefetchSlots,
  prefetchContracts
} from '../prefetch/api';
import { getActiveCompanySlug } from '../utils/companySlug';
import { useLang } from '../contexts/LanguageContext';

const baseUrl = process.env.REACT_APP_API_URL || window.location.origin;

/* ----------------------------- lazy pages ----------------------------- */
const Dashboard = lazyWithPreload(() => import(/* webpackPrefetch: true */ '../pages/Dashboard'));
const Clients = lazyWithPreload(() => import(/* webpackPrefetch: true */ '../pages/Clients'));
const AgentsPage = lazyWithPreload(() => import(/* webpackPrefetch: true */ '../pages/AgentsPage'));
const LeadsPage = lazyWithPreload(() => import(/* webpackPrefetch: true */ '../pages/LeadsPage'));
const ManagerTasksPage = lazyWithPreload(() => import(/* webpackPrefetch: true */ '../pages/ManagerTasksPage'));
const ManagerLeadsPage = lazyWithPreload(() => import(/* webpackPrefetch: true */ '../pages/ManagerLeadsPage'));
const ManagersPage = lazyWithPreload(() => import(/* webpackPrefetch: true */ '../pages/managers'));
const AdminTasksPage = lazyWithPreload(() => import(/* webpackPrefetch: true */ '../pages/AdminTasksPage'));
const SlotsAdmin = lazyWithPreload(() => import(/* webpackPrefetch: true */ '../pages/SlotsAdmin'));
const AdminOrdersPage = lazyWithPreload(() => import(/* webpackPrefetch: true */ '../pages/AdminOrdersPage'));
const AdminProposalsPage = lazyWithPreload(() => import(/* webpackPrefetch: true */ '../pages/AdminProposalsPage'));
const ContractsList = lazyWithPreload(() => import(/* webpackPrefetch: true */ '../pages/ContractsList'));
const VendorsList = lazyWithPreload(() => import(/* webpackPrefetch: true */ '../pages/VendorsList'));

/* ----------------------------- types ----------------------------- */
interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

type NavItem = {
  to: string;
  text: string;
  icon: React.ReactNode;
  roles?: string[];
  permAll?: string[];
  permAny?: string[];
  feature?: string; // Feature key for company feature control
  disabled?: boolean;
};

type SidebarLinkProps = {
  to: string;
  icon: React.ReactNode;
  text: string;
  isActive?: boolean;
  onClick?: () => void;
  onWarm?: () => void;
  collapsed?: boolean;
  disabled?: boolean;
  badge?: number | null; // Badge count to display
};

/* ----------------------------- helpers ----------------------------- */

const setSidebarCssWidth = (rem: '16rem' | '4.5rem') => {
  document.documentElement.style.setProperty('--sidebar-w', rem);
  // optional: let anyone else listen if they want
  window.dispatchEvent(new Event('sidebar:width'));
};

/**
 * Determines if a navigation item should be visible
 * 
 * CRITICAL PRIORITY ORDER (company restrictions override everything):
 * 1. Company-level restrictions (hiddenFromSidebar) - OVERRIDES all role permissions
 * 2. Role-based visibility (superadmin, admin, manager, etc.)
 * 3. Permission-based visibility (permAll, permAny)
 * 4. Feature-based visibility (hasFeature)
 * 
 * @param item - Navigation item to check
 * @param role - User role
 * @param permissions - User permissions array
 * @param hasFeature - Function to check if feature is enabled
 * @param hiddenFromSidebar - Array of feature keys hidden by SuperAdmin (COMPANY-LEVEL RESTRICTION)
 * @param user - User object for additional checks
 * @returns true if item should be visible, false otherwise
 */
function canSee(
  item: NavItem, 
  role: string | undefined, 
  permissions: string[], 
  hasFeature: (feature: string) => boolean,
  hiddenFromSidebar: string[] = [],
  user?: { role?: string; email?: string; permissions?: string[] } | null
) {
  // CRITICAL: Company-level restrictions override ALL role permissions
  // If SuperAdmin has hidden this feature, NO tenant user (admin/manager/agent) can see it
  if (item.feature && hiddenFromSidebar.includes(item.feature)) {
    return false; // Company restriction - OVERRIDES everything
  }
  
  // Superadmin items are ONLY for superadmin - never show to regular admins or managers
  if (item.roles && item.roles.includes('superadmin') && role !== 'superadmin') {
    return false;
  }
  
  // Check if manager has full access (all permissions) - they should see everything like admin
  const isFullAccessManager = role === 'manager' && hasFullPermissions(user?.permissions || permissions || []);
  
  // Admin-only items: admins, superadmins, main account, OR managers with full access can see them
  if (item.roles && item.roles.includes('admin') && !item.roles.includes('manager')) {
    // This is an admin-only item (not shared with managers)
    // But managers with full access can see it
    if (isFullAccessManager) {
      // Manager with full access can see admin items
    } else if (!canSeeAdminContent(user || { role, email: undefined })) {
      return false;
    }
  }
  
  // Admins and superadmins: only see items that include their role
  // This prevents admins from seeing manager/agent-specific items
  if (role === 'admin' || role === 'superadmin') {
    // If item has roles defined, check if admin/superadmin is included
    if (item.roles && item.roles.length > 0) {
      // For superadmin: only show superadmin items
      if (role === 'superadmin') {
        return item.roles.includes('superadmin');
      }
      // For admin: show admin items, but NOT manager-only or agent-only items
      // Allow items that include 'admin' role (even if shared with other roles)
      // BUT exclude items that are ONLY for manager or agent (without admin)
      if (role === 'admin') {
        // Show if item includes 'admin' role
        // This prevents showing manager-only items (roles: ['manager']) or agent-only items (roles: ['agent'])
        const hasAdmin = item.roles.includes('admin');
        const isManagerOnly = item.roles.length === 1 && item.roles.includes('manager');
        const isAgentOnly = item.roles.length === 1 && item.roles.includes('agent');
        
        // Allow if it has admin role, but exclude if it's manager-only or agent-only
        return hasAdmin && !isManagerOnly && !isAgentOnly;
      }
    }
    // If no roles defined, allow it (for backward compatibility)
    return true;
  }
  
  // Managers with full access: see everything like admin (admin items + manager items)
  if (isFullAccessManager) {
    // Show admin items and manager items
    if (item.roles && item.roles.length > 0) {
      return item.roles.includes('admin') || item.roles.includes('manager');
    }
    return true;
  }
  
  // Check role access - managers can NEVER access admin-only items
  if (item.roles && !item.roles.includes(role || '')) {
    return false;
  }
  
  // Permission checks for managers (and other non-admin roles)
  // Ensure permissions is always an array for safe checking
  const safePermissions = Array.isArray(permissions) ? permissions : [];
  
  if (item.permAll && item.permAll.length > 0) {
    const hasAllPerms = item.permAll.every(p => safePermissions.includes(p));
    if (!hasAllPerms) {
      return false;
    }
  }
  if (item.permAny && item.permAny.length > 0) {
    const hasAnyPerm = item.permAny.some(p => safePermissions.includes(p));
    // Debug log for proposals visibility - always log for debugging
    if (item.to?.includes('proposals') && role === 'manager') {
    }
    if (!hasAnyPerm) {
      return false;
    }
  }
  
  // Check feature access using plan features + enabled features
  // NOTE: hiddenFromSidebar check is already done at the top of this function
  // This is a secondary check for feature enablement
  if (item.feature) {
    // Use hasFeature which combines plan features with enabled features
    // This ensures plan-based access control
    const hasFeatureAccess = hasFeature(item.feature);
     
    return hasFeatureAccess;
  }
  
  return true;
}


function useNavItems() {
  const { client } = useClient();
  const params = useParams();
  const location = useLocation();
  const { t } = useLang();
  const role = client?.role;
  
  // CRITICAL FIX: Handle superadmin routes properly
  // If we're on a superadmin route or user is superadmin, don't use company param
  const isSuperadminRoute = location.pathname.startsWith('/superadmin') || role === 'superadmin';
  const company = isSuperadminRoute ? null : (params.company ?? getActiveCompanySlug());
  
  // CRITICAL: Prevent undefined/empty company from creating /undefined routes
  // For superadmin, base should be null (routes use /superadmin directly, not ${base}/...)
  // For regular users, ensure company is valid before creating base
  // Using null instead of empty string prevents creating routes like /home when base is empty
  const base = isSuperadminRoute ? null : (company && company !== 'undefined' && company.trim() ? `/${company}` : null);
  
  const permissions: string[] = Array.isArray(client?.permissions) ? (client?.permissions || []) : [];
  const email = client?.email?.toLowerCase();
  const { hasFeature, hiddenFromSidebar, isReady, loading } = useCompanyFeatures();
  
  // Check if user has same rules as vp@wjsinnovations.com
  const isVPEmail = email === 'vp@wjsinnovations.com';
  
  // Check if manager has full access (all permissions)
  const isFullAccessManager = role === 'manager' && hasFullPermissions(permissions);
  
  // Create user object for role checks
  const userForChecks = { role, email, permissions };

  // Memoize items array to prevent recreation on every render
  // CRITICAL: Only create routes with ${base}/... if base is not null (not superadmin)
  const items: NavItem[] = useMemo(() => {
    // If base is null (superadmin), only return superadmin items (they use /superadmin directly)
    if (base === null) {
      return [
        // Superadmin - all routes use /superadmin directly (not ${base}/superadmin)
        { to: `/superadmin/home`, text: t('nav.home'), icon: <FiMonitor />, roles: ['superadmin'] },
        { to: `/superadmin/companies`, text: t('nav.companies'), icon: <FiBriefcase />, roles: ['superadmin'] },
        { to: `/superadmin/trial-management`, text: t('nav.trialManagement'), icon: <FiClock />, roles: ['superadmin'] },
        { to: `/superadmin/active-clients`, text: t('nav.activeClients'), icon: <FiUsers />, roles: ['superadmin'] },
        { to: `/superadmin/overview`, text: t('nav.overview'), icon: <FiBarChart2 />, roles: ['superadmin'] },
        { to: `/superadmin/infrastructure`, text: t('nav.infrastructure'), icon: <FiServer />, roles: ['superadmin'] },
        { to: `/superadmin/announcements`, text: t('nav.announcements'), icon: <FiBell />, roles: ['superadmin'] },
        { to: `/superadmin/logs`, text: t('nav.serverLogs'), icon: <FiFileText />, roles: ['superadmin'] },
        { to: `/superadmin/audit-logs`, text: t('nav.auditLogs'), icon: <FiShield />, roles: ['superadmin'] },
        { to: `/superadmin/device-management`, text: t('nav.deviceManagement'), icon: <FiMonitor />, roles: ['superadmin'] },
        { to: `/superadmin/online-users`, text: t('nav.onlineUsers'), icon: <FiUsers />, roles: ['superadmin'] },
        { to: `/superadmin/billing`, text: t('nav.billing'), icon: <FiDollarSign />, roles: ['superadmin'] },
        { to: `/superadmin/white-label`, text: t('nav.whiteLabel'), icon: <FiDroplet />, roles: ['superadmin'] },
        { to: `/superadmin/automation-limits`, text: t('nav.automationLimits'), icon: <FiZap />, roles: ['superadmin'] },
        { to: `/superadmin/global-users`, text: t('nav.globalUsers'), icon: <FiUsers />, roles: ['superadmin'] },
        { to: `/superadmin/company-features`, text: t('nav.companyFeatures'), icon: <FiCheckSquare />, roles: ['superadmin'], feature: 'company-features' },
        { to: `/superadmin/cms-pages`, text: t('nav.cmsPages'), icon: <FiFileText />, roles: ['superadmin'] },
        { to: `/superadmin/mail`, text: t('nav.mail'), icon: <FiMail />, roles: ['superadmin'] },
        { to: `/superadmin/guide-admin`, text: t('nav.guideAdmin'), icon: <FiHelpCircle />, roles: ['superadmin'] },
      ];
    }
    
    // For non-superadmin users, create routes with base prefix
    return [
    // Client (also for admin, manager, agent)
    { to: `${base}/home`, text: t('nav.home'), icon: <FiMonitor />, roles: ['client', 'admin', 'manager', 'agent', 'vendor'] },
    { to: `${base}/appointments`, text: t('nav.appointments'), icon: <FiClock />, roles: ['client'] },
    { to: `${base}/calendar`, text: t('nav.graphicCalendar'), icon: <FiCalendar />, roles: ['client'] },
    { to: `${base}/my-profile`, text: t('nav.profile'), icon: <span>👤</span>, roles: ['client'] },
    { to: `${base}/my-orders`, text: t('nav.myOrders'), icon: <FiShoppingCart />, roles: ['client'] },

    // Agent
    { to: `${base}/agents`, text: t('nav.potential'), icon: <FaCarSide />, roles: ['agent'], feature: 'agents' },
    { to: `${base}/agent/settings`, text: t('nav.settings'), icon: <IoSettingsOutline />, roles: ['agent'], feature: 'settings' },

    // Admin
    { to: `${base}/dashboard`, text: t('nav.dashboard'), icon: <FiBarChart2 />, roles: ['admin'], feature: 'dashboard' },
    { to: `${base}/clients`, text: company === 'dr-morads' ? t('nav.patients') : t('nav.clients'), icon: <FiUsers />, roles: ['admin'], feature: 'clients' },
    { to: `${base}/contracts`, text: t('nav.contracts'), icon: <FiFileText />, roles: ['admin'] },
    { to: `${base}/vendors`, text: t('nav.vendors'), icon: <FiTruck />, roles: ['admin'] },
    { to: `${base}/managers`, text: t('nav.users'), icon: <FaUsersIcon />, roles: ['admin'], feature: 'managers' },
    { to: `${base}/agents`, text: t('nav.agents'), icon: <FaCarSide />, roles: ['admin'], feature: 'agents' },
    { to: `${base}/leads`, text: t('nav.leads'), icon: <SiGoogleads  />, roles: ['admin'], feature: 'leads' },
    { to: `${base}/admin/orders`, text: t('nav.orders'), icon: <FiShoppingCart />, roles: ['admin'], feature: 'orders' },
    { to: `${base}/admin/proposals`, text: t('nav.priceOffers'), icon: <FiFileText />, roles: ['admin'] },
    { to: `${base}/admin/inventory`, text: t('nav.inventory'), icon: <FiPackage />, roles: ['admin'], feature: 'inventory' },
    { to: `${base}/admin/slots`, text: t('nav.slots'), icon: <FiClock />, roles: ['admin'], feature: 'slots' },
    { to: `${base}/admin/tasks`, text: t('nav.tasks'), icon: <FiCheckSquare />, roles: ['admin'], feature: 'tasks' },
    { to: `${base}/admin/workers`, text: t('nav.workersAndShifts'), icon: <FiBriefcase />, roles: ['admin'] },
    { to: `${base}/admin/calendar`, text: t('nav.calendar'), icon: <IoCalendarNumberOutline />, roles: ['admin'], feature: 'calendar' },
    { to: `${base}/admin/automations`, text: t('nav.automations'), icon: <FiZap />, roles: ['admin'], feature: 'automations' },
    { to: `${base}/admin/settings`, text: t('nav.settings'), icon: <IoSettingsOutline />, roles: ['admin'], feature: 'settings' },

    // Manager (regular - own data only)
    { to: `${base}/manager-crm`, text: company === 'dr-morads' ? t('nav.myPatients') : t('nav.myClients'), icon: <FaUsersIcon />, roles: ['manager'] },
    { to: `${base}/dashboard`, text: t('nav.dashboard'), icon: <FiBarChart2 />, roles: ['manager'], permAll: ['overview'], feature: 'dashboard' },
    { to: `${base}/clients`, text: company === 'dr-morads' ? t('nav.patientsManagement') : t('nav.clientsManagement'), icon: <FiUsers />, roles: ['manager'], permAll: ['manage_clients'], feature: 'clients' },
    { to: `${base}/leads/my`, text: t('nav.myLeads'), icon: <SiGoogleads  />, roles: ['manager'], permAny: ['view_leads'], feature: 'leads' },
    { to: `${base}/admin/orders`, text: t('nav.orders'), icon: <FiShoppingCart />, roles: ['manager'], permAll: ['manage_orders'], feature: 'orders' },
    { to: `${base}/admin/proposals`, text: t('nav.priceOffers'), icon: <FiFileText />, roles: ['manager'], permAny: ['view_proposals'] },
    { to: `${base}/contracts`, text: t('nav.contracts'), icon: <FiFileText />, roles: ['manager'] },
    { to: `${base}/vendors`, text: t('nav.vendors'), icon: <FiTruck />, roles: ['manager'], permAll: ['view_vendors'] },
    { to: `${base}/manager/tasks`, text: t('nav.myTasks'), icon: <LiaTasksSolid />, roles: ['manager'], permAll: ['view_tasks'], feature: 'tasks' },
    { to: `${base}/manager/shifts`, text: t('nav.myShifts'), icon: <FiClock />, roles: ['manager'] },
    { to: `${base}/managers`, text: t('nav.users'), icon: <FaUsersIcon />, roles: ['manager'], permAll: ['view_manager'], feature: 'managers' },
    { to: `${base}/agents`, text: t('nav.agents'), icon: <FaCarSide />, roles: ['manager'], permAny: ['view_agent', 'view_potential_clients'], feature: 'agents' },
    { to: `${base}/admin/slots`, text: t('nav.slots'), icon: <FiClock />, roles: ['manager'], permAll: ['view_slots'], feature: 'slots' },
    { to: `${base}/manager/settings`, text: t('nav.settings'), icon: <IoSettingsOutline />, roles: ['manager'], feature: 'settings' },

    // Vendor (supplier portal)
    { to: `${base}/vendor/me`, text: t('nav.vendorProfile'), icon: <FiTruck />, roles: ['vendor'] },
    { to: `${base}/admin/proposals`, text: t('nav.priceOffers'), icon: <FiFileText />, roles: ['vendor'] },
    { to: `${base}/vendor/catalog`, text: t('nav.catalogManagement'), icon: <FiPackage />, roles: ['vendor'] },
    { to: `${base}/contracts`, text: t('nav.contracts'), icon: <FiFileText />, roles: ['vendor'] },

    // Common
    { to: `${base}/issues`, text: t('nav.support'), icon: <FiHelpCircle />, roles: ['admin', 'manager', 'agent', 'client', 'vendor'], feature: 'issues' },
    { to: `${base}/about`, text: t('nav.about'), icon: <FcAbout />, feature: 'about' },
    { to: `${base}/privacy`, text: t('nav.privacy'), icon: <FiShield />, roles: ['admin', 'manager', 'agent', 'client', 'vendor'] },
    { to: `${base}/terms`, text: t('nav.terms'), icon: <FiFileText />, roles: ['admin', 'manager', 'agent', 'client', 'vendor'] },
    ];
  }, [base, company, t]);

  // Debug: Log when hiddenFromSidebar changes (after items and userForChecks are declared)
  useEffect(() => {  
    // Debug: Check agents visibility
    const agentsItem = items.find(item => item.feature === 'agents');

  }, [hiddenFromSidebar, company, items, role, permissions, hasFeature, userForChecks]);
  
  // Listen for force refresh events
  useEffect(() => {
    const handleForceRefresh = (event: CustomEvent) => {
      const eventDetail = event.detail || {};
      const eventCompany = eventDetail.company;
    };
    window.addEventListener('force-sidebar-refresh', handleForceRefresh as EventListener);
    return () => {
      window.removeEventListener('force-sidebar-refresh', handleForceRefresh as EventListener);
    };
  }, [company]);

  // Memoize filtered items to prevent recalculation on every render
  const { plan } = useCompanyFeatures();
  
  // CRITICAL: Safe items that are always allowed (no feature restrictions)
  const SAFE_ITEMS = ['home', 'dashboard', 'settings', 'my-profile', 'client/settings', 'manager/settings', 'agent/settings', 'admin/settings'];
  
  return useMemo(() => {
    const visible: NavItem[] = [];
    const seenPaths = new Set<string>(); // Track seen paths to prevent duplicates
    const seenTexts = new Set<string>(); // Track seen texts to prevent duplicates
    
    // CRITICAL: If features are not ready yet, only show safe items (no feature restrictions)
    // This prevents restricted tabs from flashing on refresh
    if (!isReady && loading) {
      
      for (const item of items) {
        // Only show safe items (home, settings, etc.) that don't have feature restrictions
        const isSafeItem = !item.feature || SAFE_ITEMS.some(safe => item.to?.includes(safe));
        
        if (!isSafeItem) {
          continue; // Skip items with features until we know what's restricted
        }
        
        // Basic visibility check (role/permissions) but skip feature check
        if (item.roles && !item.roles.includes(role || '')) {
          continue;
        }
        
        // Check permissions
        const safePermissions = Array.isArray(permissions) ? permissions : [];
        if (item.permAll && item.permAll.length > 0) {
          const hasAllPerms = item.permAll.every(p => safePermissions.includes(p));
          if (!hasAllPerms) continue;
        }
        if (item.permAny && item.permAny.length > 0) {
          const hasAnyPerm = item.permAny.some(p => safePermissions.includes(p));
          if (!hasAnyPerm) continue;
        }
        
        // Add safe item
        const itemPath = item.to?.startsWith('#') ? item.to : (base ? `${base}${item.to}` : item.to);
        if (itemPath && !seenPaths.has(itemPath)) {
          seenPaths.add(itemPath);
          seenTexts.add(item.text);
          visible.push({ ...item, disabled: false });
        }
      }
      
      return visible;
    }
    
    
    const hiddenTabs: string[] = [];
    
    for (const item of items) {
      // Check visibility first (this will filter out superadmin items from regular admins)
      // Managers are NEVER allowed to see admin-only items
      // CRITICAL: canSee() checks hiddenFromSidebar FIRST (company restriction override)
      const isVisible = canSee(item, role, permissions, hasFeature, hiddenFromSidebar, userForChecks);
      
      if (!isVisible) {
        // Track which tabs are being hidden
        if (item.feature && hiddenFromSidebar.includes(item.feature)) {
          hiddenTabs.push(item.feature);
        }
        continue;
      }
      
      // CRITICAL: Superadmin should see ALL their items (already filtered by canSee above)
      // Skip the admin filtering for superadmin
      if (role === 'superadmin') {
        // Superadmin items are already filtered by canSee() which checks item.roles.includes('superadmin')
        // Just add it to visible items without additional filtering
        if (!seenPaths.has(item.to)) {
          visible.push(item);
          seenPaths.add(item.to);
        }
        continue;
      }
      
      // For admin, VP email, OR manager with full access: filter to show only specific items
      if (role === 'admin' || isVPEmail || isFullAccessManager) {
        // List of allowed items for admin/VP
        const allowedItems = [
          'home',
          'dashboard',
          'clients',
          'contracts',
          'vendors',
          'managers',
          'agents',
          'leads',
          'orders',
          'proposals',
          'inventory',
          'slots',
          'tasks',
          'workers',
          'calendar',
          'automations',
          'settings',
          'issues',
          'about',
          'terms',
          'privacy'
        ];
        
        // Check if item should be shown
        // CRITICAL: base should never be null here (superadmin items are filtered earlier)
        // But add safety check to prevent errors
        const itemPath = base ? item.to.replace(base, '').replace(/^\//, '') : item.to.replace(/^\//, '');
        const normalizedPath = itemPath.toLowerCase();
        
        // Define allowed paths and texts for admin/VP
        const allowedPaths = [
          'home',
          'dashboard',
          'clients',
          'contracts',
          'vendors',
          'managers',
          'agents',
          'leads',
          'admin/orders',
          'admin/proposals',
          'admin/inventory',
          'admin/slots',
          'admin/tasks',
          'admin/workers',
          'admin/calendar',
          'admin/automations',
          'admin/settings',
          'manager/shifts',
          'issues',
          'about',
          'terms',
          'privacy'
        ];
        
        const allowedTexts = [
          t('nav.home'),
          t('nav.dashboard'),
          t('nav.clients'),
          t('nav.clientsManagement'),
          t('nav.contracts'),
          t('nav.vendors'),
          t('nav.users'),
          t('nav.agents'),
          t('nav.leads'),
          t('nav.orders'),
          t('nav.priceOffers'),
          t('nav.inventory'),
          t('nav.slots'),
          t('nav.tasks'),
          t('nav.workersAndShifts'),
          t('nav.myShifts'),
          t('nav.calendar'),
          t('nav.automations'),
          t('nav.settings'),
          t('nav.support'),
          t('nav.about'),
          t('nav.terms'),
          t('nav.privacy')
        ];
        
        // Skip items that should be hidden
        const skipItems = [t('nav.stickyNotes'), t('nav.calculator'), t('nav.chat'), 'sticky-notes', 'calculator', 'chat'];
        if (skipItems.some(skip => item.text.includes(skip) || item.feature === skip)) {
          continue;
        }
        
        // Skip hash routes (sticky-notes, calculator, chat)
        if (item.to.startsWith('#')) {
          continue;
        }
        
        // Check if item is allowed by path or text
        const isAllowedByPath = allowedPaths.some(allowed => normalizedPath.includes(allowed.toLowerCase()));
        const isAllowedByText = allowedTexts.some(allowed => item.text === allowed);
        
        if (!isAllowedByPath && !isAllowedByText) {
          continue;
        }
        
        // Prevent duplicates: if we've already seen this path, skip it
        // For admin, prefer admin items over manager/agent items with same path
        if (seenPaths.has(item.to)) {
          // If we already have this path, check if current item is better (admin role)
          const existingIndex = visible.findIndex(v => v.to === item.to);
          if (existingIndex >= 0) {
            const existing = visible[existingIndex];
            // Prefer admin items over manager/agent items
            const currentIsAdmin = item.roles?.includes('admin');
            const existingIsAdmin = existing.roles?.includes('admin');
            if (currentIsAdmin && !existingIsAdmin) {
              // Replace with admin version
              visible[existingIndex] = { ...item, disabled: false };
            }
          }
          continue;
        }
        
        // Also check for duplicate texts, but only if path is different
        if (seenTexts.has(item.text) && item.text !== t('nav.home') && item.text !== t('nav.support')) {
          // Skip duplicate text unless it's a common item like home or support
          continue;
        }
        
        seenPaths.add(item.to);
        seenTexts.add(item.text);
        visible.push({ ...item, disabled: false });
        continue;
      }
      
      // Block orders for Basic plan companies
      if (item.feature === 'orders' && plan && plan.toLowerCase() === 'basic') {
        continue; // Skip orders items for Basic plan
      }
      
      // Prevent duplicates: if we've already seen this path, skip it
      if (seenPaths.has(item.to)) {
        // If we already have this path, check if current item matches role better
        const existingIndex = visible.findIndex(v => v.to === item.to);
        if (existingIndex >= 0) {
          const existing = visible[existingIndex];
          // Prefer items that match the user's role exactly
          const currentMatchesRole = item.roles?.includes(role || '');
          const existingMatchesRole = existing.roles?.includes(role || '');
          
          if (currentMatchesRole && !existingMatchesRole) {
            // Replace with item that matches role better
            const disabled = Boolean(
              item.feature &&
              !hasFeature(item.feature)
            );
            visible[existingIndex] = { ...item, disabled };
          }
          // Otherwise keep the existing one
        }
        continue;
      }
      
      // Also check for duplicate texts with different paths (but allow common items)
      if (seenTexts.has(item.text) && item.text !== t('nav.home') && item.text !== t('nav.support') && item.text !== t('nav.about') && item.text !== t('nav.privacy') && item.text !== t('nav.terms')) {
        // Skip duplicate text unless it's a common item
        continue;
      }
      
      seenPaths.add(item.to);
      seenTexts.add(item.text);
      
      // Item is disabled if it has a feature and that feature is not available
      const disabled = Boolean(
        item.feature &&
        !hasFeature(item.feature)
      );
      visible.push({ ...item, disabled });
    }

    return visible;
  }, [items, role, permissions, hasFeature, hiddenFromSidebar, plan, email, isVPEmail, base, userForChecks, company, isReady, loading, t]);
}



function SidebarLink({ to, icon, text, isActive, onClick, onWarm, collapsed, disabled, isOnHomePage, badge }: SidebarLinkProps & { isOnHomePage?: boolean }) {
  const { guardedNavigate } = useUnsavedChanges();
  const { t } = useLang();

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();

    if (disabled) {
      return;
    }
    // Handle special links (e.g., #sticky-notes, #chat, #lumira-ai)
    if (to.startsWith('#')) {
      if (to === '#chat') {
        window.dispatchEvent(new CustomEvent('open-chat'));
      } else if (to === '#lumira-ai') {
        window.dispatchEvent(new CustomEvent('open-lumira-ai'));
      } else {
        const event = to.substring(1); // Remove the '#'
        window.dispatchEvent(new CustomEvent(event, { detail: { action: 'toggle' } }));
      }
      onClick?.();
      return;
    }

    onWarm?.();
    guardedNavigate(to);
    onClick?.();
  };

  const handleDragStart = (e: React.DragEvent) => {
    // Only allow dragging when on Home page
    if (!isOnHomePage || disabled) {
      e.preventDefault();
      return;
    }
    // Store the route and text in the drag data
    e.dataTransfer.effectAllowed = 'copy';
    e.dataTransfer.setData('application/json', JSON.stringify({
      route: to,
      text: text,
      type: 'sidebar-item'
    }));
    // Add visual feedback
    (e.target as HTMLElement).style.opacity = '0.5';
  };

  const handleDragEnd = (e: React.DragEvent) => {
    (e.target as HTMLElement).style.opacity = '1';
  };

  // Determine if item should be draggable (only on Home page and not disabled)
  const isDraggable = isOnHomePage && !disabled;

  const stateClasses = disabled
    ? 'bg-transparent text-gray-400 cursor-not-allowed'
    : isActive
      ? 'bg-blue-50 text-blue-700 border-r-4 border-blue-500'
      : 'hover:bg-gray-50 text-gray-700';

  const title = collapsed ? (disabled ? `${text} (${t('nav.lockedInPlan')})` : text) : undefined;

  // Tooltip when collapsed
  return (
    <Link
      to={to}
      onClick={handleClick}
      onMouseEnter={disabled ? undefined : onWarm}
      onFocus={disabled ? undefined : onWarm}
      draggable={isDraggable}
      onDragStart={isDraggable ? handleDragStart : undefined}
      onDragEnd={isDraggable ? handleDragEnd : undefined}
      className={`
        group relative flex items-center gap-3 rounded-lg px-3 py-2.5 
        transition-all duration-200 font-medium
        touch-manipulation
        focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1
        ${stateClasses} 
        ${isDraggable ? 'cursor-grab active:cursor-grabbing' : !disabled ? 'cursor-pointer' : ''}
        ${!disabled ? 'hover:shadow-sm active:scale-[0.98] hover:bg-opacity-50' : ''}
      `}
      style={{ minHeight: collapsed ? '44px' : '48px' }}
      title={title}
      aria-disabled={disabled || undefined}
      tabIndex={disabled ? -1 : undefined}
    >
      <span className={`${collapsed ? 'text-xl' : 'text-lg'} shrink-0 flex items-center justify-center w-5 h-5 relative`}>
        {icon}
        {collapsed && badge !== null && badge !== undefined && badge > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[16px] h-4 px-1.5 flex items-center justify-center shadow-md ring-2 ring-white">
            {badge > 99 ? '99+' : badge}
          </span>
        )}
      </span>
      {!collapsed && (
        <span className="flex-1 flex items-center justify-between gap-2 text-sm sm:text-base truncate min-w-0">
          <span className="truncate">{text}</span>
          {badge !== null && badge !== undefined && badge > 0 && (
            <span className="flex-shrink-0 bg-red-500 text-white text-xs font-bold rounded-full min-w-[20px] h-5 px-2 flex items-center justify-center shadow-md ring-2 ring-white">
              {badge > 99 ? '99+' : badge}
            </span>
          )}
          {disabled && (
            <span className="flex items-center gap-1 text-xs text-gray-400 flex-shrink-0 ml-1">
              <FiLock className="w-3.5 h-3.5" />
              {t('nav.locked')}
            </span>
          )}
        </span>
      )}

      {/* custom tooltip on hover for collapsed state */}
      {collapsed && !disabled && (
        <span className="pointer-events-none absolute right-full top-1/2 -translate-y-1/2 mr-2 whitespace-nowrap rounded-md bg-gray-900 px-2 py-1 text-xs text-white opacity-0 transition group-hover:opacity-100 shadow">
          {text}
        </span>
      )}
      {collapsed && disabled && (
        <span className="pointer-events-none absolute right-full top-1/2 -translate-y-1/2 mr-2 whitespace-nowrap rounded-md bg-gray-900 px-2 py-1 text-xs text-white opacity-0 transition group-hover:opacity-100 shadow">
          {text} ({t('nav.locked')})
        </span>
      )}
    </Link>
  );
}

// Badge logic
type BadgeType = 'trial' | 'basic' | 'vip' | 'platinum' | 'enterprise' | null;

const getCompanyBadge = (enabledFeatures: string[] | null = [], companyPlan: string | null = null): BadgeType => {
  // PRIORITY 1: Check plan first - plan-based badges take precedence over features
  // This ensures correct badge is shown even if features haven't been saved yet
  // Normalize plan to lowercase for comparison
  const normalizedPlan = companyPlan ? companyPlan.toLowerCase() : null;
  
  if (normalizedPlan === 'enterprise') {
    return 'enterprise';
  }
  
  // PRIORITY 2: Check for platinum plan
  if (normalizedPlan === 'platinum') {
    return 'platinum';
  }
  
  // PRIORITY 3: Check for VIP plan
  if (normalizedPlan === 'vip') {
    return 'vip';
  }
  
  // PRIORITY 4: Check for basic plan
  if (normalizedPlan === 'basic') {
    return 'basic';
  }
  
  // PRIORITY 5: If plan is trial, always return trial badge regardless of features
  // This ensures new trial accounts show trial badge, not VIP/platinum
  if (normalizedPlan === 'trial' || normalizedPlan === 'trial_plus' || normalizedPlan === 'free') {
    return 'trial';
  }

  if (enabledFeatures === null) {
    // If plan is enterprise, return enterprise; otherwise platinum
    return normalizedPlan === 'enterprise' ? 'enterprise' : 'platinum';
  }
  
  // Basic features
  const basicFeatures = ['dashboard', 'clients', 'managers', 'tasks', 'calculator', 'calendar', 'settings', 'issues', 'about'];
  // VIP features (in addition to basic)
  const vipFeatures = ['agents', 'slots', 'sms'];
  // Trial features (limited subset)
  const trialFeatures = ['dashboard', 'clients', 'managers', 'tasks', 'calendar', 'settings', 'issues', 'about'];
  // All features (must include all features for platinum)
  // IMPORTANT: This list must match exactly with the features defined in superadmin-svc
  const allFeatures = [
    'dashboard', 'clients', 'managers', 'agents', 'leads', 'orders', 
    'inventory', 'slots', 'tasks', 'calendar', 'automations', 'sticky-notes', 'calculator', 
    'chat', 'lumira-ai', 'contract-ai', 'sms', 'company-features', 'settings', 'issues', 'about', 'landing-page'
  ];

  // If empty array, treat as all features enabled (backward compatibility)
  if (enabledFeatures.length === 0) {
    return normalizedPlan === 'enterprise' ? 'enterprise' : 'platinum';
  }

  // Check if has all features (platinum/enterprise)
  const hasAllFeatures = allFeatures.every(feature => enabledFeatures.includes(feature));
  if (hasAllFeatures) {
    // Check plan to distinguish between enterprise and platinum
    return normalizedPlan === 'enterprise' ? 'enterprise' : 'platinum';
  }

  // Check if has basic + VIP features
  const hasBasic = basicFeatures.every(feature => enabledFeatures.includes(feature));
  const hasVip = vipFeatures.every(feature => enabledFeatures.includes(feature));
  if (hasBasic && hasVip) {
    return 'vip';
  }

  // Check if has basic features
  if (hasBasic) {
    return 'basic';
  }

  // Check if has trial features only - but only if plan is actually trial
  // Don't return trial if plan is set to something else (platinum, vip, basic, enterprise)
  const hasTrial = trialFeatures.every(feature => enabledFeatures.includes(feature)) && 
                   enabledFeatures.length === trialFeatures.length;
  if (hasTrial && (normalizedPlan === 'trial' || normalizedPlan === 'trial_plus' || normalizedPlan === 'free' || !normalizedPlan)) {
    return 'trial';
  }
  
  // If plan is set but features don't match, still respect the plan
  if (normalizedPlan === 'platinum') return 'platinum';
  if (normalizedPlan === 'vip') return 'vip';
  if (normalizedPlan === 'basic') return 'basic';

  return null;
};

const BadgeIcon: React.FC<{ type: BadgeType; size?: 'sm' | 'md' | 'lg'; className?: string }> = ({ type, size = 'md', className = '' }) => {
  const { t } = useLang();
  if (!type) return null;

    const sizeClasses = {
      sm: 'w-12 h-12',
      md: 'w-14 h-14',
      lg: 'w-20 h-20'
    };
  
  const iconSizes = {
    sm: 'w-3 h-3',
    md: 'w-4 h-4',
    lg: 'w-5 h-5'
  };
  
  switch (type) {
    case 'enterprise':
      return (
        <div 
          className={`${sizeClasses[size]} ${className} relative flex items-center justify-center`}
          style={{ zIndex: 9999 }}
          title={t('nav.enterpriseUnlimited')}
        >
          <style>{`
            @keyframes luxuryPulse {
              0%, 100% { 
                opacity: 1;
                filter: drop-shadow(0 4px 12px rgba(255, 215, 0, 0.6)) drop-shadow(0 0 20px rgba(255, 215, 0, 0.4));
              }
              50% { 
                opacity: 0.9;
                filter: drop-shadow(0 6px 16px rgba(255, 215, 0, 0.8)) drop-shadow(0 0 30px rgba(255, 215, 0, 0.6));
              }
            }
          `}</style>
          <div className="relative w-full h-full flex items-center justify-center" style={{ zIndex: 10000 }}>
            {/* Luxury Infinity Symbol with emoji in center */}
            <svg 
              viewBox="0 0 100 100" 
              className="w-full h-full absolute"
              style={{ 
                animation: 'luxuryPulse 2s ease-in-out infinite',
                zIndex: 10001
              }}
            >
              <defs>
                <linearGradient id="luxuryGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#FFD700" stopOpacity="1" />
                  <stop offset="50%" stopColor="#FFA500" stopOpacity="1" />
                  <stop offset="100%" stopColor="#FF6347" stopOpacity="1" />
                </linearGradient>
                <linearGradient id="luxuryGradient2" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#FF6347" stopOpacity="1" />
                  <stop offset="50%" stopColor="#FFD700" stopOpacity="1" />
                  <stop offset="100%" stopColor="#FFA500" stopOpacity="1" />
                </linearGradient>
              </defs>
              {/* Infinity symbol path */}
              <path
                d="M 20 50 Q 20 20, 50 20 Q 80 20, 80 50 Q 80 80, 50 80 Q 20 80, 20 50 M 80 50 Q 80 20, 50 20 Q 20 20, 20 50 Q 20 80, 50 80 Q 80 80, 80 50"
                fill="none"
                stroke="url(#luxuryGradient)"
                strokeWidth="8"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M 20 50 Q 20 20, 50 20 Q 80 20, 80 50"
                fill="none"
                stroke="url(#luxuryGradient2)"
                strokeWidth="6"
                strokeLinecap="round"
                opacity="0.8"
              />
            </svg>
            {/* Infinity emoji in center */}
            <span 
              className="text-2xl sm:text-3xl font-bold relative"
              style={{
                zIndex: 10002,
                textShadow: '0 2px 8px rgba(255, 215, 0, 0.8), 0 0 16px rgba(255, 215, 0, 0.6)',
                filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))'
              }}
            >
              ♾️
            </span>
          </div>
        </div>
      );
    case 'platinum':
      return (
        <div 
          className={`${sizeClasses[size]} ${className} relative`}
          title={t('nav.platinumAllFeatures')}
        >
            <img 
              src="/platinum.png" 
              alt="Platinum Badge"
              className="w-full h-full object-contain"
              style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))' }}
            />
        </div>
      );
    case 'vip':
      return (
        <div 
          className={`${sizeClasses[size]} ${className} relative`}
          title={t('nav.vipAdvanced')}
        >
            <img 
              src="/gold.png" 
              alt="VIP Badge"
              className="w-full h-full object-contain"
              style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))' }}
            />
        </div>
      );
    case 'basic':
      return (
        <div 
          className={`${sizeClasses[size]} ${className} relative`}
          title={t('nav.basicBasic')}
        >
            <img 
              src="/bronze.png" 
              alt="Basic Badge"
              className="w-full h-full object-contain"
              style={{ filter: 'drop-shadow(0 2px 8px rgba(255, 165, 0, 0.5))' }}
            />
        </div>
      );
    case 'trial':
      return (
        <div 
          className={`${sizeClasses[size]} ${className} relative`}
          title={t('nav.trialLimited')}
        >
          <div className="w-full h-full rounded-full bg-gray-400 border-2 border-gray-500 flex items-center justify-center text-white font-bold">
            T
          </div>
        </div>
      );
    default:
      return null;
  }
};

// Sidebar header component with toggle button and badge
function SidebarHeader({ collapsed, toggleCollapsed }: { collapsed: boolean; toggleCollapsed: () => void }) {
  const { company } = useParams();
  const { client } = useClient();
  const { t } = useLang();
  const { enabledFeatures, loading, plan } = useCompanyFeatures();
  const [badgeType, setBadgeType] = useState<BadgeType>(null);

  useEffect(() => {
    if (!company || company === 'superadmin' || client?.role === 'superadmin') {
      setBadgeType('platinum');
      return;
    }
    // Don't show badge until plan is loaded
    if (loading || !plan) {
      setBadgeType(null);
      return;
    }
    const featuresArray = enabledFeatures === null ? null : enabledFeatures;
    const calculatedBadge = getCompanyBadge(featuresArray ?? [], plan);
    setBadgeType(calculatedBadge);
  }, [company, client?.role, enabledFeatures, plan, loading]);

  return (
    <div className="flex items-center justify-between gap-2 px-2 py-3 border-b border-gray-100/80 bg-white/60 sticky top-0 z-10 backdrop-blur-sm shadow-sm">
      {/* Toggle button on the left */}
      <button
        onClick={toggleCollapsed}
        className="
          h-9 w-9 grid place-items-center rounded-lg 
          text-gray-500 hover:text-gray-700 hover:bg-gray-100 
          active:bg-gray-200 transition-all duration-200
          focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1
          shadow-sm hover:shadow-md touch-manipulation flex-shrink-0
        "
        aria-label={collapsed ? t('nav.expandSidebar') : t('nav.collapseSidebar')}
        title={collapsed ? t('nav.expandSidebar') : t('nav.collapseSidebar')}
      >
        {collapsed ? <FiChevronLeft className="text-base" /> : <FiChevronRight className="text-base" />}
      </button>
      
      {/* Badge with label on the right (only show when not collapsed) */}
      {!collapsed && badgeType && (
        <div className="flex-shrink-0 flex items-center gap-2">
          <div 
            className={`inline-flex items-center justify-center gap-1 px-2.5 py-1 rounded-full font-bold text-[11px] uppercase tracking-wider shadow-md ${
              badgeType === 'enterprise' 
                ? 'bg-gradient-to-r from-yellow-400 via-amber-500 to-orange-600 text-white border border-yellow-300/50'
                : badgeType === 'platinum' 
                ? 'bg-black text-white border border-white/20' 
                : badgeType === 'vip'
                ? 'bg-gradient-to-r from-yellow-400 via-yellow-500 to-yellow-600 text-white border border-yellow-300/50'
                : badgeType === 'basic'
                ? 'bg-gradient-to-r from-amber-600 via-amber-700 to-amber-800 text-white border border-amber-400/50'
                : badgeType === 'trial'
                ? 'bg-gradient-to-r from-gray-500 via-gray-600 to-gray-700 text-white border border-gray-400/50'
                : 'bg-gray-400 text-white'
            }`}
            style={{
              boxShadow: badgeType === 'enterprise'
                ? '0 4px 12px rgba(255, 215, 0, 0.6), inset 0 1px 0 rgba(255,255,255,0.3), 0 0 20px rgba(255, 215, 0, 0.4)'
                : badgeType === 'platinum' 
                ? '0 2px 4px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.1)'
                : badgeType === 'vip'
                ? '0 2px 4px rgba(245, 158, 11, 0.4), inset 0 1px 0 rgba(255,255,255,0.3)'
                : badgeType === 'basic'
                ? '0 2px 4px rgba(180, 83, 9, 0.4), inset 0 1px 0 rgba(255,255,255,0.2)'
                : badgeType === 'trial'
                ? '0 2px 4px rgba(107, 114, 128, 0.4), inset 0 1px 0 rgba(255,255,255,0.2)'
                : '0 2px 4px rgba(0,0,0,0.2)'
            }}
          >
            {badgeType === 'enterprise' && <span className="text-white font-bold">∞ ENTERPRISE</span>}
            {badgeType === 'platinum' && <span className="text-white font-bold">PLATINUM</span>}
            {badgeType === 'vip' && <span className="text-white font-bold">VIP</span>}
            {badgeType === 'basic' && <span className="text-white font-bold">BASIC</span>}
            {badgeType === 'trial' && <span className="text-white font-bold">TRIAL</span>}
          </div>
          <BadgeIcon type={badgeType} size="sm" />
        </div>
      )}
    </div>
  );
}

function LogoSection({ collapsed }: { collapsed: boolean }) {
  const { company } = useParams();
  const { client } = useClient();
  const { enabledFeatures, loading, plan } = useCompanyFeatures();
  const [badgeType, setBadgeType] = useState<BadgeType>(null);

  useEffect(() => {
    if (!company || company === 'superadmin' || client?.role === 'superadmin') {
      setBadgeType('platinum');
      return;
    }
    // Don't show badge until plan is loaded
    if (loading || !plan) {
      setBadgeType(null);
      return;
    }
    const featuresArray = enabledFeatures === null ? null : enabledFeatures;
    const calculatedBadge = getCompanyBadge(featuresArray ?? [], plan);
    setBadgeType(calculatedBadge);
  }, [company, client?.role, enabledFeatures, plan, loading]);

  return (
    <div className="flex flex-col items-center mb-6 relative">
      <img
        src="/crmLogo.png"
        alt="CRM Logo"
        onError={e => ((e.target as HTMLImageElement).style.display = 'none')}
        className={`rounded-2xl shadow bg-gray-200 object-cover ${collapsed ? 'w-10 h-10' : 'w-14 h-14'} transition-all`}
      />
      {/* Badge positioned behind logo */}
      {badgeType && !collapsed && (
        <div className="absolute -bottom-1 -right-1 z-10">
{/*           <BadgeIcon type={badgeType} size="sm" />
*/}        </div>
      )}
      {!collapsed && (
        <div className="relative mt-2">
          <h2
            className="text-2xl font-extrabold text-blue-700 tracking-widest leading-none"
            style={{ letterSpacing: '.08em' }}
          >
            WJS 365ᵈ Together
          </h2>
          <div className="text-[10px] text-gray-500 text-center mt-0.5 flex flex-col items-center gap-0">
            <div className="flex items-center justify-center gap-1">
              <span>By WJS Digital Innovations</span>
              {badgeType && (
                <BadgeIcon type={badgeType} size="sm" className="ml-1" />
              )}
            </div>
            {badgeType && (
              <div 
                className={`inline-flex items-center justify-center gap-1.5 px-2.5 py-0.5 rounded-full font-bold text-[9px] uppercase tracking-wider shadow-md ${
                  badgeType === 'enterprise' 
                    ? 'bg-gradient-to-r from-yellow-400 via-amber-500 to-orange-600 text-white border border-yellow-300/50'
                    : badgeType === 'platinum' 
                    ? 'bg-black/90 text-white border border-white/20' 
                    : badgeType === 'vip'
                    ? 'bg-gradient-to-r from-yellow-400 via-yellow-500 to-yellow-600 text-yellow-900 border border-yellow-300/50'
                    : badgeType === 'basic'
                    ? 'bg-gradient-to-r from-amber-600 via-amber-700 to-amber-800 text-amber-50 border border-amber-400/50'
                    : badgeType === 'trial'
                    ? 'bg-gradient-to-r from-gray-500 via-gray-600 to-gray-700 text-white border border-gray-400/50'
                    : 'bg-gradient-to-r from-amber-600 via-amber-700 to-amber-800 text-amber-50 border border-amber-400/50'
                }`}
                style={{
                  boxShadow: badgeType === 'enterprise'
                    ? '0 4px 12px rgba(255, 215, 0, 0.6), inset 0 1px 0 rgba(255,255,255,0.3), 0 0 20px rgba(255, 215, 0, 0.4)'
                    : badgeType === 'platinum' 
                    ? '0 2px 6px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.1)'
                    : badgeType === 'vip'
                    ? '0 2px 6px rgba(245, 158, 11, 0.5), inset 0 1px 0 rgba(255,255,255,0.3)'
                    : badgeType === 'basic'
                    ? '0 2px 6px rgba(180, 83, 9, 0.5), inset 0 1px 0 rgba(255,255,255,0.2)'
                    : badgeType === 'trial'
                    ? '0 2px 6px rgba(107, 114, 128, 0.5), inset 0 1px 0 rgba(255,255,255,0.2)'
                    : '0 2px 6px rgba(180, 83, 9, 0.5), inset 0 1px 0 rgba(255,255,255,0.2)',
                  marginTop: '-3px',
                  marginLeft: '-30px'
                }}
              >
                {badgeType === 'enterprise' && <span className="text-white">∞ Enterprise</span>}
                {badgeType === 'platinum' && <span className="text-white">Platinum</span>}
                {badgeType === 'vip' && <span className="text-yellow-900">VIP</span>}
                {badgeType === 'basic' && <span className="text-amber-50">Basic</span>}
                {badgeType === 'trial' && <span className="text-white">Trial</span>}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/* ----------------------------- component ----------------------------- */
const Sidebar: React.FC<SidebarProps> = ({ isOpen, onClose }) => {
  const { t } = useLang();
  const navItems = useNavItems();
  const location = useLocation();
  const navigate = useNavigate();
  const { client } = useClient();
  const params = useParams();
  
  // CRITICAL FIX: For superadmin, company should be null (not 'superadmin' or undefined)
  // This prevents creating /undefined routes in warm object and other places
  const isSuperadminRoute = location.pathname.startsWith('/superadmin') || client?.role === 'superadmin';
  const company = isSuperadminRoute ? null : (params.company || getActiveCompanySlug() || null);
  
  // Ensure company is not 'undefined' string
  const validCompany = company && company !== 'undefined' && company.trim() ? company : null;
  
  // Calculate base for guide link
  const base = isSuperadminRoute ? null : (validCompany ? `/${validCompany}` : null);

  // detect header height so the sidebar starts **below** the header
  const [headerH, setHeaderH] = useState(0);
  useEffect(() => {
    const findHeader = () =>
      (document.querySelector('[data-app-header]') as HTMLElement)
      || (document.getElementById('app-header') as HTMLElement);

    const measure = () => {
      const el = findHeader();
      setHeaderH(el ? Math.ceil(el.getBoundingClientRect().height) : 0);
    };
    measure();
    const ro = new ResizeObserver(measure);
    const hdr = findHeader();
    if (hdr) ro.observe(hdr);
    window.addEventListener('resize', measure);
    return () => { window.removeEventListener('resize', measure); ro.disconnect(); };
  }, []);

  // collapsed state (desktop), persisted
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    const v = localStorage.getItem('sidebar:collapsed');
    return v === '1';
  });

  useEffect(() => {
    // set initial var based on persisted state
    const collapsed = localStorage.getItem('sidebar:collapsed') === '1';
    const initial: '16rem' | '4.5rem' = collapsed ? '4.5rem' : '16rem';
    setSidebarCssWidth(initial);
  }, []);

  const toggleCollapsed = () => {
    setCollapsed(c => {
      const next = !c;
      localStorage.setItem('sidebar:collapsed', next ? '1' : '0');
      const width: '16rem' | '4.5rem' = next ? '4.5rem' : '16rem';
      setSidebarCssWidth(width); // <-- publish width
      return next;
    });
  };
  const isActive = useCallback((path: string) => location.pathname === path, [location.pathname]);
  
  // Check if we're on the Home page - sidebar items should only be draggable on Home page
  const isOnHomePage = useMemo(() => {
    const pathname = location.pathname;
    // Check for regular company home page: /{company}/home
    if (validCompany && pathname === `/${validCompany}/home`) {
      return true;
    }
    // Check for superadmin home page: /superadmin/home
    if (pathname === '/superadmin/home') {
      return true;
    }
    return false;
  }, [location.pathname, validCompany]);

  // prewarm for routes
  const warm = useMemo(() => {
    // CRITICAL: Only create warm routes if we have a valid company (not superadmin)
    if (!validCompany || validCompany === 'undefined' || validCompany.trim() === '') {
      return {} as Record<string, () => void>;
    }
    return {
      [`/${validCompany}/dashboard`]: () => { Dashboard.preload?.(); prefetchDashboard(validCompany); },
      [`/${validCompany}/clients`]: () => { Clients.preload?.(); prefetchClients(validCompany, { page: 1, pageSize: 25 }); },
      [`/${validCompany}/agents`]: () => { AgentsPage.preload?.(); prefetchAgents(validCompany, { page: 1, pageSize: 10 }); },
      [`/${validCompany}/leads`]: () => { LeadsPage.preload?.(); /* prefetchLeads?.(validCompany) if you add one */ },
      [`/${validCompany}/manager/tasks`]: () => { ManagerTasksPage.preload?.(); prefetchManagerTasks(validCompany); },
      [`/${validCompany}/leads/my`]: () => { ManagerLeadsPage.preload?.(); prefetchManagerLeads(validCompany); },
      [`/${validCompany}/managers`]: () => { ManagersPage.preload?.(); prefetchManagers(validCompany); },
      [`/${validCompany}/admin/tasks`]: () => { AdminTasksPage.preload?.(); prefetchAdminTasks(validCompany); },
      [`/${validCompany}/admin/slots`]: () => { SlotsAdmin.preload?.(); prefetchSlots(validCompany); },
      [`/${validCompany}/admin/leads`]: () => { LeadsPage.preload?.(); prefetchAdminLeads(validCompany); },
      [`/${validCompany}/admin/orders`]: () => { AdminOrdersPage.preload?.(); },
      [`/${validCompany}/admin/proposals`]: () => { AdminProposalsPage.preload?.(); },
      [`/${validCompany}/contracts`]: () => {
        ContractsList.preload?.();
        // Prefetch contracts using React Query (proper prefetch function)
        prefetchContracts(validCompany, { page: 1, limit: 25 });
      },
      [`/${validCompany}/vendors`]: () => { VendorsList.preload?.(); },
    };
  }, [validCompany]);

  // prime some pages on mount for admin/manager based on role and permissions
  useEffect(() => {
    // CRITICAL: Skip prefetching for superadmin (no company) or if no valid company
    if (!validCompany || !client?.role || client.role === 'superadmin') return;
    const role = client.role;
    const permissions = client.permissions || [];

    const canWarm = (path: string) => navItems.some(item => item.to === path && !item.disabled);
    
    // Common pages for all roles
    if (canWarm(`/${validCompany}/dashboard`)) {
      warm[`/${validCompany}/dashboard`]?.();
    }
    
    if (role === 'admin') {
      // Admin pages
      if (canWarm(`/${validCompany}/clients`)) warm[`/${validCompany}/clients`]?.();
      if (canWarm(`/${validCompany}/agents`)) warm[`/${validCompany}/agents`]?.();
      if (canWarm(`/${validCompany}/leads`)) warm[`/${validCompany}/leads`]?.();
      if (canWarm(`/${validCompany}/managers`)) warm[`/${validCompany}/managers`]?.();
      if (canWarm(`/${validCompany}/admin/tasks`)) warm[`/${validCompany}/admin/tasks`]?.();
      if (canWarm(`/${validCompany}/admin/slots`)) warm[`/${validCompany}/admin/slots`]?.();
      if (canWarm(`/${validCompany}/admin/leads`)) warm[`/${validCompany}/admin/leads`]?.();
      if (canWarm(`/${validCompany}/admin/proposals`)) warm[`/${validCompany}/admin/proposals`]?.();
      if (canWarm(`/${validCompany}/vendors`)) warm[`/${validCompany}/vendors`]?.();
    } else if (role === 'manager') {
      // Manager pages based on permissions
      if (permissions.includes('view_clients') && canWarm(`/${validCompany}/clients`)) {
        warm[`/${validCompany}/clients`]?.();
      }
      if ((permissions.includes('view_agent') || permissions.includes('view_potential_clients')) && canWarm(`/${validCompany}/agents`)) {
        warm[`/${validCompany}/agents`]?.();
      }
      if (permissions.includes('view_leads') && canWarm(`/${validCompany}/leads/my`)) {
        warm[`/${validCompany}/leads/my`]?.();
      }
      if (permissions.includes('view_tasks') && canWarm(`/${validCompany}/manager/tasks`)) {
        warm[`/${validCompany}/manager/tasks`]?.();
      }
      if (permissions.includes('view_manager') && canWarm(`/${validCompany}/managers`)) {
        warm[`/${validCompany}/managers`]?.();
      }
      if (permissions.includes('view_slots') && canWarm(`/${validCompany}/admin/slots`)) {
        warm[`/${validCompany}/admin/slots`]?.();
      }
      if (permissions.includes('view_proposals') && canWarm(`/${validCompany}/admin/proposals`)) {
        warm[`/${validCompany}/admin/proposals`]?.();
      }
      if (permissions.includes('view_vendors') && canWarm(`/${validCompany}/vendors`)) {
        warm[`/${validCompany}/vendors`]?.();
      }
      if (canWarm(`/${validCompany}/vendors`)) warm[`/${validCompany}/vendors`]?.();
    } else if (role === 'agent') {
      // Agent pages
      if (canWarm(`/${validCompany}/agents`)) {
        warm[`/${validCompany}/agents`]?.();
      }
    } else if (role === 'client') {
      // Client pages - no additional prefetching needed for now
    }
  }, [validCompany, client?.role, client?.permissions, warm, navItems]);

  // Unresolved issues count for superadmin badge
  const [unresolvedIssuesCount, setUnresolvedIssuesCount] = useState<number>(0);

  // Fetch unresolved issues count for superadmin
  useEffect(() => {
    if (client?.role !== 'superadmin') {
      setUnresolvedIssuesCount(0);
      return;
    }

    const fetchUnresolvedCount = async () => {
      try {
        const rpcIssues = (await import('../api/rpc/rpcIssues')).default;
        const response = await rpcIssues.countUnresolved();
        if (response?.data?.ok && typeof response.data.data?.count === 'number') {
          setUnresolvedIssuesCount(response.data.data.count);
        }
      } catch (err) {
        console.error('[Sidebar] Failed to fetch unresolved issues count:', err);
      }
    };

    fetchUnresolvedCount();

    // Refresh count when navigating to/from issues page
    if (location.pathname === '/superadmin/issues') {
      // Refresh count when viewing issues page
      fetchUnresolvedCount();
    }

    // Listen for socket events about new issues
    const handleNewIssue = () => {
      fetchUnresolvedCount();
    };

    // Listen for issue updates (when issues are resolved)
    const handleIssueUpdate = () => {
      fetchUnresolvedCount();
    };

    // Listen to window events for issue updates
    // These are dispatched from SocketBridge when socket events are received
    window.addEventListener('issue:created', handleNewIssue);
    window.addEventListener('issue:updated', handleIssueUpdate);
    window.addEventListener('issue:status-changed', handleIssueUpdate);

    // Periodic refresh every 30 seconds as fallback
    const intervalId = setInterval(() => {
      fetchUnresolvedCount();
    }, 30000);

    // Refresh when page becomes visible again
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchUnresolvedCount();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('issue:created', handleNewIssue);
      window.removeEventListener('issue:updated', handleIssueUpdate);
      window.removeEventListener('issue:status-changed', handleIssueUpdate);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      clearInterval(intervalId);
    };
  }, [client?.role, location.pathname]);

  // logout
  const [confirm, setConfirm] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const confirmLogout = async () => {
    // Close modal immediately
    setConfirm(false);
    
    // Show logout overlay immediately
    setIsLoggingOut(true);
    
    // Force a re-render to ensure overlay is painted
    await new Promise(resolve => {
      requestAnimationFrame(() => {
        requestAnimationFrame(resolve);
      });
    });
    
    // Set flag to indicate this is a manual logout (not session revoked)
    // IMPROVED: Also store timestamp to detect recent manual logout
    const logoutTimestamp = Date.now().toString();
    sessionStorage.setItem('manualLogout', 'true');
    localStorage.setItem('manualLogout', 'true');
    sessionStorage.setItem('manualLogoutTime', logoutTimestamp);
    localStorage.setItem('manualLogoutTime', logoutTimestamp);
    
    // CRITICAL: Clear justLoggedIn flag to prevent interceptor from ignoring 401 errors
    sessionStorage.removeItem('justLoggedIn');
    sessionStorage.removeItem('justLoggedInTime');
    sessionStorage.removeItem('justLoggedInCompany');
    
    // Determine if this is superadmin (from URL or client role)
    const isSuperadmin = location.pathname.startsWith('/superadmin') || client?.role === 'superadmin';
    const companyForLogout = isSuperadmin ? 'superadmin' : (validCompany || '');
        
    if (!companyForLogout) {
      console.error('❌ No company found for logout');
      // Force logout anyway - clear cache and storage
      queryClient.clear();
      localStorage.removeItem('token');
      localStorage.removeItem('accessToken');
      localStorage.removeItem('client');
      localStorage.removeItem('currentCompany');
      localStorage.removeItem('activeTenantId');
      localStorage.removeItem('activeTenantName');
      localStorage.removeItem('activeRole');
      localStorage.removeItem('lastLogin');
      localStorage.removeItem('lastActivity');
      // Use logoutSuperadminToLogin to ensure it goes through forceLogout fuse
      logoutSuperadminToLogin('Sidebar::handleLogout_no_company', {
        reason: 'manual_click',
      });
      return;
    }
    
    try {
      await logout(companyForLogout, 'sidebar-logout');
    } catch (err) {
      console.error('❌ Logout API call failed:', err);
      // Continue with logout even if API call fails
    }
    
    // IMPORTANT: Clear all React Query cache and CRM-related localStorage on logout
    // to ensure next login starts from a clean slate
    try {
      // 1) Clear all React Query caches (queries + mutations)
      queryClient.clear();
      
      // 2) Clear all CRM-related auth and company data from localStorage
      // Keep user preferences like rememberEmail, rememberPassword, device_id
      const oldClient = localStorage.getItem('client');
      if (oldClient) {
        try {
          const parsed = JSON.parse(oldClient);
        } catch (e) {
          console.warn('[Logout] Could not parse client data');
        }
      }
      localStorage.removeItem('token');
      localStorage.removeItem('accessToken');
      localStorage.removeItem('client');
      localStorage.removeItem('currentCompany');
      localStorage.removeItem('activeTenantId');
      localStorage.removeItem('activeTenantName');
      localStorage.removeItem('activeRole');
      localStorage.removeItem('lastLogin');
      localStorage.removeItem('lastActivity');
      localStorage.removeItem('wjs_active_company_v2');
      localStorage.removeItem('wjs_superadmin_auth');
      
      // 2.5) CRITICAL: Clear cookies that might persist
      // Use centralized auth cookie clear helper so flags (domain, secure, sameSite)
      // match the backend's setAuthCookie/clearAuthCookie behavior.
      if (typeof document !== 'undefined') {
        import('../utils/authCookies').then(({ clearAllAuthCookies }) => {
          clearAllAuthCookies();
        }).catch(err => {
          console.error('[Sidebar] Error importing authCookies:', err);
        });
      }
      
      // 3) For superadmin, redirect to /superadmin/login, otherwise to /{company}/login
      if (isSuperadmin) {
        logoutSuperadminToLogin('Sidebar::handleLogout_superadmin', {
          reason: 'manual_click',
        });
      } else if (validCompany) {
        // For company routes, use forceLogout directly with manual_click
        const forceLogout = createForceLogout(queryClient);
        forceLogout({
          reason: 'manual_click',
          source: 'Sidebar::handleLogout_company',
          companySlug: validCompany,
        });
      } else {
        logoutSuperadminToLogin('Sidebar::handleLogout_fallback', {
          reason: 'manual_click',
        });
      }
    } catch (redirectErr) {
      console.error('❌ Redirect failed:', redirectErr);
      // Force redirect as last resort - use logoutSuperadminToLogin
      logoutSuperadminToLogin('Sidebar::handleLogout_error_fallback', {
        reason: 'manual_click',
      });
    }
  };

  /* ----------------------------- render ----------------------------- */
  const desktopWidth = collapsed ? 'w-20' : 'w-64';

  return (
    <>
      {/* Desktop Sidebar (fixed below header, flexible height) */}
      <aside
        className="hidden md:flex flex-col fixed right-0 bg-white/98 backdrop-blur-sm border-l border-gray-200/80 shadow-lg z-[45] transition-all duration-300 ease-in-out"
        style={{
          width: collapsed ? '4.5rem' : '16rem',
          top: headerH && headerH > 0 ? `${headerH}px` : '52px',
          height: headerH && headerH > 0 ? `calc(100vh - ${headerH}px)` : 'calc(100vh - 52px)',
          maxHeight: headerH && headerH > 0 ? `calc(100vh - ${headerH}px)` : 'calc(100vh - 52px)',
          overflowY: 'auto',
          overflowX: 'hidden',
          WebkitOverflowScrolling: 'touch',
          scrollbarWidth: 'thin',
          scrollbarColor: '#cbd5e1 transparent'
        }}
      >
        {/* Toggle Arrow Button and Badge - inside sidebar at the top */}
        <SidebarHeader collapsed={collapsed} toggleCollapsed={toggleCollapsed} />

        <nav className="flex flex-col gap-1 text-gray-700 flex-grow min-h-0 overflow-y-auto px-2 py-2.5" style={{ scrollbarWidth: 'thin', scrollbarColor: '#cbd5e1 transparent' }}>
          {navItems.map(item => (
            <SidebarLink
              key={item.to}
              to={item.to}
              icon={item.icon}
              text={item.text}
              isActive={!item.disabled && isActive(item.to)}
              onWarm={item.disabled ? undefined : warm[item.to]}
              onClick={() => { }}
              collapsed={collapsed}
              disabled={item.disabled}
              isOnHomePage={isOnHomePage}
            />
          ))}

          {client?.role === 'superadmin' && (
            <SidebarLink
              to="/superadmin/issues"
              icon={<FiHelpCircle />}
              text={t('nav.issuesManagement')}
              isActive={isActive('/superadmin/issues')}
              onWarm={() => { }}
              onClick={() => { }}
              collapsed={collapsed}
              isOnHomePage={isOnHomePage}
              badge={unresolvedIssuesCount > 0 ? unresolvedIssuesCount : null}
            />
          )}
        </nav>

        <div className="mt-auto pt-3 pb-3 border-t border-gray-100/80 bg-white/60 sticky bottom-0 backdrop-blur-sm px-2.5 shadow-sm space-y-2">
          {/* Guide Link - Always visible to all users at bottom */}
          {base ? (
            <SidebarLink
              to={`${base}/guide`}
              icon={<FiHelpCircle />}
              text={t('nav.guideCenter')}
              isActive={isActive(`${base}/guide`)}
              onWarm={() => {}}
              onClick={() => {}}
              collapsed={collapsed}
              isOnHomePage={isOnHomePage}
            />
          ) : client?.role === 'superadmin' ? (
            <SidebarLink
              to="/superadmin/guide-admin"
              icon={<FiHelpCircle />}
              text={t('nav.guideAdmin')}
              isActive={isActive('/superadmin/guide-admin')}
              onWarm={() => {}}
              onClick={() => {}}
              collapsed={collapsed}
              isOnHomePage={isOnHomePage}
            />
          ) : null}
          
          <button
            onClick={() => setConfirm(true)}
            className={`
              flex items-center gap-2 text-red-600 hover:text-red-700 hover:bg-red-50 
              active:bg-red-100 rounded-lg px-3 py-2.5 transition-all duration-200 
              font-medium w-full touch-manipulation
              focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-1
              shadow-sm hover:shadow-md
              ${collapsed ? 'justify-center' : ''}
            `}
            title={t('nav.logout')}
          >
            <FiLogOut className="text-base flex-shrink-0" /> {!collapsed && <span>{t('nav.logout')}</span>}
          </button>
        </div>
      </aside>

      {/* Mobile Drawer + overlay */}
      <div
        className={`fixed inset-0 z-[110] md:hidden transition-all duration-300 ease-out ${
          isOpen ? 'visible' : 'pointer-events-none invisible'
        }`}
      >
        <div
          className={`absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity duration-300 ${
            isOpen ? 'opacity-100' : 'opacity-0'
          }`}
          onClick={onClose}
          aria-label={t('nav.closeMenu')}
          aria-hidden={!isOpen}
        />
        <div
          className={`absolute right-0 top-0 h-full w-[85vw] max-w-sm bg-white 
            rounded-l-3xl shadow-2xl flex flex-col transition-transform duration-300 ease-out
            ${isOpen ? 'translate-x-0' : 'translate-x-full'}
            border-l border-gray-200/80
          `}
          role="dialog"
          aria-modal="true"
          aria-label={t('nav.navMenu')}
          {...(!isOpen && { inert: true })}
        >
          <div className="sticky top-0 bg-white border-b border-gray-200/80 px-4 py-4 flex items-center justify-between z-10 shadow-sm">
            <div className="flex items-center gap-3 min-w-0 flex-1">
              <img
                src="/crmLogo.png"
                alt="CRM Logo"
                className="w-11 h-11 rounded-lg border border-slate-200/80 shadow-md bg-white flex-shrink-0"
              />
              <div className="min-w-0 flex-1">
                <div className="font-bold text-slate-900 text-base truncate">WJS 365ᵈ Together</div>
                <div className="text-xs text-slate-500 truncate">By WJS Digital Innovations</div>
              </div>
            </div>
            <button
              className="
                text-gray-400 hover:text-red-600 text-xl 
                p-2 rounded-lg hover:bg-red-50 active:bg-red-100
                transition-all duration-200
                active:scale-95 touch-manipulation
                focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2
                min-w-[44px] min-h-[44px] flex items-center justify-center flex-shrink-0
              "
              onClick={onClose}
              aria-label={t('nav.closeMenu')}
            >
              <FiX />
            </button>
          </div>

          <nav className="flex flex-col gap-1 text-gray-700 flex-grow min-h-0 overflow-y-auto px-3 py-3" style={{ scrollbarWidth: 'thin', scrollbarColor: '#cbd5e1 transparent' }}>
            {navItems.map(item => (
              <SidebarLink
                key={item.to}
                to={item.to}
                icon={item.icon}
                text={item.text}
                isActive={!item.disabled && isActive(item.to)}
                onWarm={item.disabled ? undefined : warm[item.to]}
                onClick={onClose}
                disabled={item.disabled}
                isOnHomePage={isOnHomePage}
              />
            ))}

            {client?.role === 'superadmin' && (
              <SidebarLink
                to="/superadmin/issues"
                icon={<FiHelpCircle />}
                text={t('nav.issuesManagement')}
                isActive={isActive('/superadmin/issues')}
                onWarm={() => { }}
                onClick={onClose}
                isOnHomePage={isOnHomePage}
              />
            )}
          </nav>

          <div className="sticky bottom-0 mt-auto pt-4 pb-4 border-t border-gray-200/80 bg-white px-3 space-y-2">
            {/* Guide Link - Always visible to all users at bottom (Mobile) */}
            {base ? (
              <SidebarLink
                to={`${base}/guide`}
                icon={<FiHelpCircle />}
                text={t('nav.guideCenter')}
                isActive={isActive(`${base}/guide`)}
                onWarm={() => {}}
                onClick={onClose}
                isOnHomePage={isOnHomePage}
              />
            ) : client?.role === 'superadmin' ? (
              <SidebarLink
                to="/superadmin/guide-admin"
                icon={<FiHelpCircle />}
                text={t('nav.guideAdmin')}
                isActive={isActive('/superadmin/guide-admin')}
                onWarm={() => {}}
                onClick={onClose}
                isOnHomePage={isOnHomePage}
              />
            ) : null}
            
            <button
              onClick={() => {
                setConfirm(true);
                onClose();
              }}
              className="
                flex items-center justify-center gap-2 
                text-red-600 hover:text-red-700 hover:bg-red-50 
                active:bg-red-100 rounded-lg px-4 py-3 
                transition-all duration-150 text-base font-semibold w-full
                touch-manipulation
                focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2
                shadow-sm hover:shadow
                min-h-[48px]
              "
            >
              <FiLogOut className="text-lg" /> {t('nav.logout')}
            </button>
          </div>
        </div>
      </div>

      {/* Logout Modal */}
      {confirm && (
        <>
          <style>{`
            @keyframes modal-fade-in {
              from {
                opacity: 0;
              }
              to {
                opacity: 1;
              }
            }
            @keyframes modal-scale-in {
              from {
                opacity: 0;
                transform: scale(0.95) translateY(-10px);
              }
              to {
                opacity: 1;
                transform: scale(1) translateY(0);
              }
            }
            @keyframes icon-shake {
              0%, 100% {
                transform: rotate(0deg);
              }
              25% {
                transform: rotate(-5deg);
              }
              75% {
                transform: rotate(5deg);
              }
            }
            @keyframes pulse-glow {
              0%, 100% {
                    box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.4);
              }
              50% {
                    box-shadow: 0 0 0 8px rgba(239, 68, 68, 0);
              }
            }
            .logout-modal-backdrop {
              animation: modal-fade-in 0.2s ease-out;
            }
            .logout-modal-content {
              animation: modal-scale-in 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
            }
            .logout-icon-animation {
              animation: icon-shake 2s ease-in-out infinite;
            }
            .logout-button-red {
              animation: pulse-glow 2s ease-in-out infinite;
            }
          `}</style>
          <div className="fixed inset-0 z-[120] logout-modal-backdrop">
            <div className="absolute inset-0 bg-gradient-to-br from-black/60 via-black/50 to-black/60 backdrop-blur-sm" onClick={() => setConfirm(false)}></div>
            <div className="relative flex items-center justify-center min-h-screen p-4">
              <div className="logout-modal-content bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden border border-gray-100">
                {/* Header with gradient */}
                <div className="bg-gradient-to-r from-red-50 via-orange-50 to-red-50 p-6 border-b border-red-100">
                  <div className="flex items-center justify-center mb-4">
                    <div className="relative">
                      <div className="w-16 h-16 rounded-full bg-gradient-to-br from-red-100 to-orange-100 flex items-center justify-center logout-icon-animation">
                        <FiLogOut className="w-8 h-8 text-red-600" />
                      </div>
                      <div className="absolute -top-1 -right-1 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center">
                        <span className="text-white text-xs font-bold">!</span>
                      </div>
                    </div>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 text-center">
                    {t('nav.logout')}
                  </h3>
                </div>
                
                {/* Content */}
                <div className="p-8 text-center">
                  <p className="text-gray-700 text-lg mb-2 font-medium">
                    {t('nav.logoutConfirm')}
                  </p>
                  <p className="text-gray-500 text-sm">
                    {t('nav.logoutRedirect')}
                  </p>
                </div>
                
                {/* Actions */}
                <div className="p-6 bg-gray-50 border-t border-gray-200 flex gap-3">
                  <button
                    onClick={() => setConfirm(false)}
                    className="flex-1 px-6 py-3 rounded-xl bg-white border-2 border-gray-300 text-gray-700 font-semibold hover:bg-gray-50 hover:border-gray-400 active:scale-95 transition-all duration-200 shadow-sm"
                  >
                    {t('common.cancel')}
                  </button>
                  <button
                    onClick={confirmLogout}
                    className="flex-1 px-6 py-3 rounded-xl logout-button-red bg-gradient-to-r from-red-600 to-red-700 text-white font-bold hover:from-red-700 hover:to-red-800 active:scale-95 transition-all duration-200 shadow-lg hover:shadow-xl"
                  >
                    {t('nav.logout')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
      
      {/* Logout Overlay - shows during logout process */}
      <LogoutOverlay visible={isLoggingOut} />
    </>
  );
};

export default Sidebar;
